import { Injectable } from '@angular/core';
import Clarity from '@microsoft/clarity';
import { environment } from 'src/environments/environment';

interface PendingIdentify {
  customId: string;
  customSessionId?: string;
  customPageId?: string;
  friendlyName?: string;
}

type ClarityTagValue = string | string[];

const MAX_READY_RETRIES = 20;
const READY_RETRY_INTERVAL_MS = 250;
const LANGUAGE_TAG_KEY = 'APP_LANGUAGE';

@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private readonly initialized: boolean;
  private consentGranted = false;
  private pendingIdentify: PendingIdentify | null = null;
  private pendingTags = new Map<string, ClarityTagValue>();

  constructor() {
    this.initialized = !!environment.clarityProjectId;
    if (this.initialized) {
      Clarity.init(environment.clarityProjectId);
    }
  }

  grantConsent(): void {
    this.consentGranted = true;
    this.applyConsent('granted');
  }

  denyConsent(): void {
    this.consentGranted = false;
    this.pendingIdentify = null;
    this.pendingTags.clear();
    this.applyConsent('denied');
  }

  identify(
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string
  ): void {
    if (this.consentGranted && this.isClarityReady()) {
      Clarity.identify(customId, customSessionId, customPageId, friendlyName);
    } else {
      this.pendingIdentify = { customId, customSessionId, customPageId, friendlyName };
    }
  }

  setTag(key: string, value: ClarityTagValue): void {
    if (this.consentGranted && this.isClarityReady()) {
      Clarity.setTag(key, value);
      this.pendingTags.delete(key);
    } else {
      this.pendingTags.set(key, value);
    }
  }

  setLanguageTag(language: string): void {
    this.setTag(LANGUAGE_TAG_KEY, language);
  }

  event(eventName: string): void {
    if (this.isClarityReady()) {
      Clarity.event(eventName);
    }
  }

  private isClarityReady(): boolean {
    return this.initialized && typeof window.clarity === 'function';
  }

  private applyConsent(analyticsStorage: 'granted' | 'denied', attempt = 0): void {
    if (!this.initialized) {
      return;
    }

    if (!this.isClarityReady()) {
      if (attempt < MAX_READY_RETRIES) {
        setTimeout(() => this.applyConsent(analyticsStorage, attempt + 1), READY_RETRY_INTERVAL_MS);
      }
      return;
    }

    window.clarity!('consentv2', {
      ad_Storage: 'denied',
      analytics_Storage: analyticsStorage,
    });

    if (analyticsStorage === 'granted') {
      this.flushPendingIdentify();
      this.flushPendingTags();
    }
  }

  private flushPendingIdentify(): void {
    if (this.pendingIdentify) {
      const { customId, customSessionId, customPageId, friendlyName } = this.pendingIdentify;
      Clarity.identify(customId, customSessionId, customPageId, friendlyName);
      this.pendingIdentify = null;
    }
  }

  private flushPendingTags(): void {
    this.pendingTags.forEach((value, key) => Clarity.setTag(key, value));
    this.pendingTags.clear();
  }
}
